/**
 * «Новая система» — заведение мини-техкарты (AK-773, Р35).
 *
 * Форма короткая намеренно: тип, название, культура и расчётная урожайность —
 * всё, что нужно, чтобы система появилась и её можно было наполнять
 * обработками. Обработки заводятся в редакторе, а не здесь: мастер из шести
 * шагов на входе в справочник — это ровно та «усталость от системы», о которой
 * говорит CLAUDE.md.
 *
 * Урожайность разбирается по правилу «пусто ≠ мусор»: пустое поле — законное
 * «не задано», «55ц» — ошибка, о которой форма говорит словами и НЕ сохраняет.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCropPicker } from '@/components/fields/card/api';
import { useCan } from '@/lib/auth/usePermissions';
import { techCardErrorText } from '@/lib/planning/techCards';
import { toast } from '@/lib/toast';
import {
  MODULE_KIND_ORDER,
  MODULE_KIND_TITLES,
  useCreateSystemModule,
  type ModuleKind,
} from '@/lib/planning/techCardModules';

/** Культура необязательна: система обработки почвы может быть общей. */
const NO_CROP = '__none__';

type Parsed = { ok: true; value: number | null } | { ok: false; error: string };

/**
 * Три исхода вместо двух: число, осознанная очистка, мусор. Пока парсер
 * типизирован как `number | null`, опечатка уезжает на сервер как «не задано».
 */
export function parseYield(raw: string): Parsed {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return { ok: true, value: null };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, error: 'Нужно число в центнерах с гектара, например 45' };
  }
  return { ok: true, value };
}

export function CreateSystemButton({
  seasonId,
  onCreated,
}: {
  seasonId: string;
  onCreated: (moduleId: string) => void;
}) {
  const canCreate = useCan('tech_cards', 'c');
  const [open, setOpen] = useState(false);
  if (!canCreate) return null;
  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        Новая система
      </Button>
      {open && (
        <CreateSystemDialog
          seasonId={seasonId}
          onClose={() => setOpen(false)}
          onCreated={(id) => {
            setOpen(false);
            onCreated(id);
          }}
        />
      )}
    </>
  );
}

export function CreateSystemDialog({
  seasonId,
  onClose,
  onCreated,
}: {
  seasonId: string;
  onClose: () => void;
  onCreated: (moduleId: string) => void;
}) {
  const [kind, setKind] = useState<ModuleKind>('protection');
  const [name, setName] = useState('');
  const [cropId, setCropId] = useState<string>(NO_CROP);
  const [minRaw, setMinRaw] = useState('');
  const [maxRaw, setMaxRaw] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const picker = useCropPicker();
  const crops = useMemo(
    () =>
      (picker.data ?? [])
        .filter((c) => !!c.crop_id)
        .map((c) => ({ id: c.crop_id as string, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [picker.data],
  );

  const create = useCreateSystemModule();

  const min = parseYield(minRaw);
  const max = parseYield(maxRaw);
  const canSubmit = !!name.trim() && min.ok && max.ok && !create.isPending;

  const submit = () => {
    if (!canSubmit || !min.ok || !max.ok) return;
    setFailure(null);
    create.mutate(
      {
        seasonId,
        kind,
        name,
        cropId: cropId === NO_CROP ? null : cropId,
        targetYieldMinCHa: min.value,
        targetYieldMaxCHa: max.value,
      },
      {
        onSuccess: (created) => {
          toast.success(`Система «${created.name}» заведена`);
          onCreated(created.id);
        },
        // Отказ показываем в открытом окне рядом с полями: набранное остаётся
        // на месте, а не пропадает вместе с диалогом.
        onError: (err) => setFailure(techCardErrorText(err)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новая система</DialogTitle>
          <DialogDescription>
            Мини-техкарта: живёт отдельно, вставляется в любую техкарту и заменяется целиком.
            Обработки добавите на следующем шаге.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="sys-kind">Тип системы</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as ModuleKind)}>
              <SelectTrigger id="sys-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODULE_KIND_ORDER.map((k) => (
                  <SelectItem key={k} value={k}>
                    {MODULE_KIND_TITLES[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="sys-name">Название</Label>
            <Input
              id="sys-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Озимая пшеница — интенсив 2027"
            />
          </div>

          <div>
            <Label htmlFor="sys-crop">Культура</Label>
            <Select value={cropId} onValueChange={setCropId}>
              <SelectTrigger id="sys-crop">
                <SelectValue placeholder="Любая культура" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CROP}>Любая культура</SelectItem>
                {crops.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="sys-ymin">Рассчитана на урожайность, от</Label>
              <Input
                id="sys-ymin"
                value={minRaw}
                onChange={(e) => setMinRaw(e.target.value)}
                aria-invalid={!min.ok}
                className={min.ok ? undefined : 'border-destructive'}
                placeholder="45"
              />
              {!min.ok && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  {min.error}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="sys-ymax">до</Label>
              <Input
                id="sys-ymax"
                value={maxRaw}
                onChange={(e) => setMaxRaw(e.target.value)}
                aria-invalid={!max.ok}
                className={max.ok ? undefined : 'border-destructive'}
                placeholder="55"
              />
              {!max.ok && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  {max.error}
                </p>
              )}
            </div>
          </div>

          {failure && (
            <p role="alert" className="text-sm text-destructive">
              {failure}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            Создать систему
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
